import { Navbar } from '../components/layout/Navbar';
import { Footer } from '../components/layout/Footer';
import { Ship, Target, Heart, Lightbulb, Users, Shield, Leaf, Award, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';

const coreValues = [
  { icon: Shield, title: 'Integrity', description: 'We operate with honesty and fairness in all our dealings.' },
  { icon: Award, title: 'Excellence', description: 'We aim for the highest standards in safety, service, and performance.' },
  { icon: Lightbulb, title: 'Innovation', description: 'We embrace new technologies to enhance efficiency and sustainability.' },
  { icon: Users, title: 'Customer Focus', description: "Our clients' success is at the center of everything we do." },
  { icon: Leaf, title: 'Sustainability', description: 'We protect the seas we sail on for future generations.' },
];

const timeline = [
  { year: '1975', title: 'Founded in Sydney', description: 'Started as a small Australian coastal shipping operator with a single vessel.' },
  { year: '1985', title: 'International Expansion', description: 'Expanded operations to Asia-Pacific trade routes.' },
  { year: '1995', title: 'Americas Gateway', description: 'Opened Los Angeles office to serve North and South American markets.' },
  { year: '2005', title: 'Global Network', description: 'Established presence in Europe and Middle East with Rotterdam and Dubai offices.' },
  { year: '2015', title: 'Fleet Modernization', description: 'Major investment in fuel-efficient, eco-friendly vessels.' },
  { year: '2025', title: '50 Years Strong', description: 'Celebrating five decades of maritime excellence and innovation.' },
];

export default function AboutPage() {
  return (
    <div className="min-h-screen bg-white" data-testid="about-page">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative py-32 lg:py-40 overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://customer-assets.emergentagent.com/job_maritime-connect-2/artifacts/eh576k8r_Copilot_20251106_002554.png')`,
          }}
        />
        <div className="absolute inset-0 hero-overlay" />
        <div className="rsl-container relative z-10">
          <span className="text-safety-orange font-mono text-xs tracking-widest uppercase">
            Our Story
          </span>
          <h1 className="text-4xl sm:text-5xl lg:text-7xl font-black text-white uppercase tracking-tight mt-2">
            Delivering Excellence <br />
            <span className="text-safety-orange">Since 1975</span>
          </h1>
          <p className="text-white/80 text-lg max-w-2xl mt-6">
            From a small Australian coastal operator to a leading global logistics provider, our journey spans five decades of maritime excellence.
          </p>
        </div>
      </section>

      {/* About Content */}
      <section className="py-20 lg:py-32">
        <div className="rsl-container">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-safety-orange font-mono text-xs tracking-widest uppercase">
                Who We Are
              </span>
              <h2 className="text-3xl lg:text-4xl font-black text-navy uppercase tracking-tight mt-2 mb-6">
                Global Reach, Local Expertise
              </h2>
              <div className="space-y-4 text-slate-600">
                <p>
                  Founded in 1975, Robert Shipping & Logistics has grown from a small Australian coastal shipping operator into a leading global cargo transport and logistics provider, trusted by businesses and industries across five continents.
                </p>
                <p>
                  For nearly five decades, we have been navigating the world's oceans, ensuring that goods of every kind — from raw materials to finished products — reach their destinations safely, efficiently, and on time.
                </p>
                <p>
                  Our story began in the busy ports of Sydney, Australia, with a single vessel and a dedicated team driven by a shared vision: to redefine maritime logistics through reliability, innovation, and integrity.
                </p>
              </div>
              <div className="flex gap-3 mt-8">
                <div className="cert-badge">
                  <span>ISO 9001</span>
                </div>
                <div className="cert-badge">
                  <span>ISO 14001</span>
                </div>
                <div className="cert-badge">
                  <span>ISM Code</span>
                </div>
              </div>
            </div>
            <div className="relative">
              <img
                src="https://customer-assets.emergentagent.com/job_maritime-connect-2/artifacts/r5hqmknb_step-img.png"
                alt="RSL Captain"
                className="w-full h-auto"
              />
              <div className="absolute -bottom-6 -left-6 bg-safety-orange p-6">
                <p className="text-white font-black text-4xl">50</p>
                <p className="text-white/80 text-sm uppercase tracking-wider">Years of Excellence</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Vision & Mission */}
      <section className="py-20 lg:py-32 bg-slate-50">
        <div className="rsl-container">
          <div className="grid lg:grid-cols-2 gap-12">
            <div className="bg-white p-8 lg:p-12 border-l-4 border-safety-orange">
              <Target className="w-12 h-12 text-safety-orange mb-6" />
              <h3 className="text-2xl font-bold text-navy mb-4">Our Vision</h3>
              <p className="text-slate-600 text-lg">
                To remain a world-class maritime logistics company, recognized for our reliability, innovation, and commitment to sustainable global trade.
              </p>
            </div>
            <div className="bg-white p-8 lg:p-12 border-l-4 border-navy">
              <Heart className="w-12 h-12 text-navy mb-6" />
              <h3 className="text-2xl font-bold text-navy mb-4">Our Mission</h3>
              <p className="text-slate-600 text-lg">
                To deliver trusted, efficient, and environmentally responsible shipping solutions that connect businesses and communities worldwide.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-20 lg:py-32">
        <div className="rsl-container">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <span className="text-safety-orange font-mono text-xs tracking-widest uppercase">
              What Drives Us
            </span>
            <h2 className="text-3xl lg:text-4xl font-black text-navy uppercase tracking-tight mt-2">
              Our Core Values
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {coreValues.map((value, index) => (
              <div key={value.title} className="value-card" data-testid={`value-${index}`}>
                <value.icon className="w-10 h-10 text-safety-orange mb-4" />
                <h3 className="text-xl font-bold text-navy mb-2">{value.title}</h3>
                <p className="text-slate-600">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-20 lg:py-32 bg-navy">
        <div className="rsl-container">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <span className="text-safety-orange font-mono text-xs tracking-widest uppercase">
              Our Journey
            </span>
            <h2 className="text-3xl lg:text-4xl font-black text-white uppercase tracking-tight mt-2">
              50 Years of Maritime Excellence
            </h2>
          </div>
          <div className="max-w-3xl mx-auto">
            {timeline.map((item, index) => (
              <div key={item.year} className="relative pl-8 pb-12 last:pb-0" data-testid={`timeline-${index}`}>
                {/* Line */}
                {index !== timeline.length - 1 && (
                  <div className="absolute left-[11px] top-6 w-0.5 h-full bg-white/20" />
                )}
                {/* Dot */}
                <div className="absolute left-0 top-1 w-6 h-6 bg-safety-orange rounded-full flex items-center justify-center">
                  <div className="w-2 h-2 bg-white rounded-full" />
                </div>
                {/* Content */}
                <div className="ml-6">
                  <span className="text-safety-orange font-mono text-sm font-bold">{item.year}</span>
                  <h3 className="text-xl font-bold text-white mt-1 mb-2">{item.title}</h3>
                  <p className="text-white/70">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-20 lg:py-32">
        <div className="rsl-container">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="order-2 lg:order-1">
              <img
                src="https://customer-assets.emergentagent.com/job_maritime-connect-2/artifacts/101hhl67_Copilot_20251106_011256.png"
                alt="RSL Team"
                className="w-full h-auto"
              />
            </div>
            <div className="order-1 lg:order-2">
              <span className="text-safety-orange font-mono text-xs tracking-widest uppercase">
                Our People
              </span>
              <h2 className="text-3xl lg:text-4xl font-black text-navy uppercase tracking-tight mt-2 mb-6">
                The RSL Family
              </h2>
              <div className="space-y-4 text-slate-600">
                <p>
                  Behind every successful voyage is a team of dedicated professionals — from experienced captains and crew to logistics experts, engineers, and customer service specialists.
                </p>
                <p>
                  We foster a culture of excellence, teamwork, and continuous improvement, ensuring that every client receives personalized service and expert attention from planning to delivery.
                </p>
                <p>
                  Many of our employees have been with us for decades, a testament to the loyalty, skill, and pride that define the Robert Shipping & Logistics family.
                </p>
              </div>
              <Link to="/contact" className="mt-8 inline-block">
                <Button className="bg-safety-orange hover:bg-orange-600 text-white font-bold uppercase tracking-widest text-sm h-12 px-8">
                  Partner With Us
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
